import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import classNames from 'classnames';
import styled from 'styled-components';

import EditSwotDataModal from 'components/View/modals/EditSwotDataModal/EditSwotDataModal';
import { COMPANY_COMMUNITY_RELATION_TYPE } from 'constants/company';
import { useAppSelector } from 'store/hook';

import { buildYAxisLabel, mergeCommunityFees } from './utils';
import { WIDGET_TYPES, WIDGETS_WITH_ALLOWED_DATA_INSERTION } from '../constants';

const Container = styled.div<{ $isOverflowing: boolean }>`
  position: relative;
  display: grid;
  grid-template-columns: repeat(auto-fill, 150px);
  gap: 5px 30px;
  margin-top: 8px;
  padding-bottom: 8px;
  height: auto;
  max-height: 96px;
  overflow-y: ${({ $isOverflowing }) => ($isOverflowing ? 'auto' : 'hidden')};
  overflow-x: visible;
  width: 100%;
  z-index: 2;

  &.mini-scroll::-webkit-scrollbar-thumb {
    height: 10px;
  }
`;

const LegendItemBox = styled.div`
  font-size: 10px;
  margin-bottom: 10px;
`;

const LegendRow = styled.div`
  display: grid;
  grid-template-columns: 12px 1fr;
  column-gap: 6px;
  align-items: start;
`;

const Marker = styled.span<{ color?: string }>`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${({ color }) => color || '#1890ff'};
`;

const Label = styled.div`
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  max-width: 130px;

  &.community {
    font-family: var(--font-bold);
  }
`;

const TooltipWrapper = styled.div`
  display: inline-block;
  cursor: pointer;
`;

const TooltipText = styled.div`
  width: max-content;
  max-width: 180px;
  background-color: #f9f9f9;
  color: #000;
  text-align: left;
  border-radius: 6px;
  padding: 6px 10px;
  position: fixed;
  z-index: 3000;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  white-space: pre-line;
  font-size: 10px;
  line-height: 1.2;
  pointer-events: none;
`;

const LegendActions = styled.div`
  margin-left: 13px;
  margin-top: 5px;
  font-size: 10px;
  color: var(--text-secondary);
  a {
    color: var(--dark-blue);
    cursor: pointer;
  }
`;

const DataInsertionLegends = ({ data, widgetType }: { data: any[]; widgetType: number }) => {
  const [tooltip, setTooltip] = useState<{
    text: string;
    left: number;
    top: number;
  } | null>(null);
  const [showInsertSwotDataModal, setShowInsertSwotDataModal] = useState(false);
  const [competitorId, setCompetitorId] = useState<number | null>(null);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);

  const closeModal = useCallback(() => setShowInsertSwotDataModal(false), [setShowInsertSwotDataModal]);

  const feePricingTypes = useAppSelector((state) => state.global.feePricingTypes ?? []);
  const livingTypes = useAppSelector((state) => state.global.livingTypes ?? []);

  const showLevelsCount = [WIDGET_TYPES.CARE_FEES_GRAPH, WIDGET_TYPES.MEDICATION_FEES_GRAPH].includes(widgetType);

  const normalisedData = mergeCommunityFees(data);
  const showDataInsertionOption = WIDGETS_WITH_ALLOWED_DATA_INSERTION.includes(widgetType);

  useEffect(() => {
    if (!containerRef.current) {
      return;
    }

    const notifyHeight = () => {
      const container = containerRef.current;
      if (!container) {
        return;
      }
      setIsOverflowing(container.scrollHeight > container.clientHeight + 1);
    };

    notifyHeight();

    const resizeObserver = new ResizeObserver(() => notifyHeight());
    resizeObserver.observe(containerRef.current);

    return () => resizeObserver.disconnect();
  }, [normalisedData.length, showInsertSwotDataModal]);

  if (!data || data.length === 0) {
    return null;
  }

  const openTooltip = (text: string, element: HTMLDivElement) => {
    const rect = element.getBoundingClientRect();
    const estimatedHeight = 56;
    const top = rect.top - estimatedHeight > 8 ? rect.top - 8 : rect.bottom + 8;
    setTooltip({
      text,
      left: rect.left,
      top,
    });
  };

  const closeTooltip = () => setTooltip(null);

  return (
    <>
      <Container className={isOverflowing ? 'mini-scroll' : ''} ref={containerRef} $isOverflowing={isOverflowing}>
        {normalisedData.map((item, index) => {
          const labelText = buildYAxisLabel({
            text: item.community_name,
            item,
            index,
            data: normalisedData,
            type: widgetType,
            livingTypes,
            feePricingTypes,
            showLevelsCount,
          });
          const isCommunity = item.community_type == COMPANY_COMMUNITY_RELATION_TYPE.COMMUNITY;

          return (
            <LegendItemBox key={item.community_id}>
              <LegendRow>
                <Marker color={item.community_color} />
                <TooltipWrapper
                  onMouseEnter={(event) => openTooltip(labelText, event.currentTarget as HTMLDivElement)}
                  onMouseLeave={closeTooltip}
                >
                  <div>
                    {labelText.split('\n').map((text: string, idx: number) => (
                      <Label
                        key={idx}
                        className={classNames({ community: isCommunity })}
                        style={{
                          color: isCommunity || idx === 0 ? 'var(--text-primary)' : 'var(--text-secondary)',
                        }}
                      >
                        {text}
                      </Label>
                    ))}
                  </div>
                </TooltipWrapper>
              </LegendRow>

              {!item.value && showDataInsertionOption && (
                <LegendActions>
                  <span>No data. </span>
                  <a
                    onClick={() => {
                      setShowInsertSwotDataModal(true);
                      setCompetitorId(item.community_id);
                    }}
                  >
                    Add some
                  </a>
                </LegendActions>
              )}
            </LegendItemBox>
          );
        })}

        {showInsertSwotDataModal && (
          <EditSwotDataModal onClose={closeModal} open={showInsertSwotDataModal} competitorId={competitorId} />
        )}
      </Container>
      {tooltip &&
        createPortal(
          <TooltipText style={{ left: tooltip.left, top: tooltip.top }}>{tooltip.text}</TooltipText>,
          document.body
        )}
    </>
  );
};

export default DataInsertionLegends;
